import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { formatNumber, dec, num } from "@/lib/formatters";
import { tooltipStyle, gruppeInfo } from "./oversiktConstants";

const mill = (v) => (v == null ? "—" : `${formatNumber(Math.round(v / 1000))} mill. kr`);
const pct = (v) => (v == null ? "—" : `${dec(v, 1)} %`);

export const PremieavvikChart = ({ data: payload, kommuneName }) => {
  const rows = payload?.rows?.filter((r) => r.pct != null);
  if (!rows?.length) return null;
  const g = gruppeInfo(payload);
  const fylkeKey = g.key;
  const utanFylke = g.utan;
  const first = rows[0];
  const last = rows[rows.length - 1];
  const diff = last.pct - first.pct;
  const vsLand = last.land_pct != null ? last.pct - last.land_pct : null;
  const chart = payload.rows.map((r) => ({
    year: r.year,
    Kommunen: r.pct,
    ...(utanFylke ? {} : { [fylkeKey]: g.pct(r) }),
    Landssnitt: r.land_pct,
    _kr: r.belop,
  }));
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="premieavvik-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-[#E8A34A]" />
          Premieavvik – mot {utanFylke ? "landssnittet" : g.storby ? "storby- og landssnitt" : "fylkes- og landssnitt"}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          Premieavviket er skilnaden mellom innbetalt pensjonspremie og rekneskapsført pensjonskostnad. Det ligg i omløpsmidlane og løftar arbeidskapitalen, men må førast som kostnad seinare år. Her er det målt i prosent av arbeidskapitalen, så du ser kor stor del av handlingsrommet som eigentleg er utsett pensjonskostnad. Lågt premieavvik er betre.
        </p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="premieavvik-kr">
            <p className="text-xs text-[#8AA4BF]">Premieavvik {last.year}</p>
            <p className="text-2xl font-light text-[#1F2A36]">{mill(last.belop)}</p>
            <p className="text-[11px] text-[#4B647F]">av ein arbeidskapital på {mill(last.arbeidskapital)}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="premieavvik-pct">
            <p className="text-xs text-[#8AA4BF]">Andel av arbeidskapitalen</p>
            <p className="text-2xl font-light text-[#1F2A36]">{pct(last.pct)}</p>
            <p className="text-[11px] text-[#4B647F]">{first.year}: {pct(first.pct)}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="premieavvik-snitt">
            <p className="text-xs text-[#8AA4BF]">{utanFylke ? "Landssnitt" : `${g.kort} og land`} {last.year}</p>
            <p className="text-2xl font-light text-[#3F8A86]">{pct(utanFylke ? last.land_pct : g.pct(last))}</p>
            <p className="text-[11px] text-[#4B647F]">{utanFylke ? "Oslo er både kommune og fylke" : `Landet: ${pct(last.land_pct)}`}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="premieavvik-vs-land">
            <p className="text-xs text-[#8AA4BF]">Mot landssnittet</p>
            <p className={`text-2xl font-light ${vsLand == null ? "text-[#1F2A36]" : vsLand > 0 ? "text-[#C0392B]" : "text-[#2F7D5B]"}`}>
              {vsLand == null ? "—" : `${vsLand > 0 ? "+" : "−"}${dec(Math.abs(vsLand), 1)} pp`}
            </p>
            <p className="text-[11px] text-[#4B647F]">{diff >= 0 ? "+" : "−"}{dec(Math.abs(diff), 1)} pp sidan {first.year}</p>
          </div>
        </div>
        <div className="h-[300px]" data-testid="premium-deviation-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chart} barCategoryGap="35%">
              {endringsLinjer(chart)}
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} width={55} tickFormatter={(v) => `${num(v)} %`} />
              <Tooltip
                formatter={(v, name, p) => (name === "Kommunen" && p.payload._kr ? `${pct(v)} (${mill(p.payload._kr)})` : pct(v))}
                contentStyle={tooltipStyle}
              />
              <Legend itemSorter={null} wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Kommunen" fill="#E8A34A" radius={[4, 4, 0, 0]} />
              {!utanFylke && <Line type="monotone" dataKey={fylkeKey} stroke="#3F8A86" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />}
              <Line type="monotone" dataKey="Landssnitt" stroke="#9CA3AF" strokeWidth={2} dot={{ r: 2 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-[#8AA4BF]">
          {utanFylke
            ? `Landssnittet er eit uvekta gjennomsnitt av dei ${last.land_count} kommunane i landet i ${last.year}. Oslo er både kommune og fylke, så noko fylkessnitt finst ikkje.`
            : `Snittene er uvekta gjennomsnitt av kommunane sine andelar av eigen arbeidskapital (${g.count(last)} ${g.count(last) === 1 ? "kommune" : "kommunar"} i ${g.ord}, ${last.land_count} i landet i ${last.year}).`}
        </p>
        <p className="text-xs text-[#8AA4BF]">
          Kjelde: SSB{" "}
          <a href="https://www.ssb.no/statbank/table/13545" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            13545 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          – balanserekneskap, kommunekasse (1000 kr), post B.3.4 Premieavvik.
        </p>
      </CardContent>
    </Card>
  );
};

export default PremieavvikChart;
